import { Component, computed, EventEmitter, inject, Input, OnDestroy, Output, signal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { StandardBottomSheetComponent } from "../../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { PhotoCaptureComponent } from "../../../../../../../shared/components/photo-capture/photo-capture.component";
import { SectionHeaderComponent } from "../../../../../../../shared/components/section-header/section-header.component";
import { FormFieldComponent } from "../../../../../../../shared/components/form-field/form-field.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { ImageType, Media } from "../../../../../../../core/database/db";
import { DomSanitizer } from "@angular/platform-browser";
import { Dialog } from "@angular/cdk/dialog";
import { BlobService } from "../../../../../../../shared/services/blob.service";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../../../domains/media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../../../domains/media/utils/media-order.util";
import { ImagesModalComponent } from "../../../../../../../domains/media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../../../../../domains/media/components/images-modal/bqa-image-item";
import { CONSTANTS } from "../../../../../../../constants";

@Component({
  selector: "app-complete-bottom-sheet",
  imports: [ReactiveFormsModule, FormFieldComponent, StandardBottomSheetComponent, PhotoCaptureComponent, SectionHeaderComponent, VerticalButtonGroupComponent],
  templateUrl: "./complete-bottom-sheet.component.html",
  styleUrl: "./complete-bottom-sheet.component.scss"
})
export class CompleteBottomSheetComponent implements OnDestroy {
  readonly maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  @Input() isOpen = false;
  @Input() itemIdentifier = "";
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<Media[]>();

  formSubmitted = false;

  form = new FormGroup({
    photos: new FormControl<Media[]>([], {
      nonNullable: true,
      validators: control => (control.value?.length ? null : { required: true })
    })
  });

  addedImages = signal<Media[]>([]);
  allImagesRaw = computed(() => this.addedImages().filter(im => !im.deleted)
    .reverse());
  allImages = computed(() =>
    this.allImagesRaw()
      .filter(img => img.base64Image || img.resourceUri)
      .map(img => {
        const azureBlobUri = img.base64Image
          ? this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image))
          : img.resourceUri!;
        return this.sanitizer.bypassSecurityTrustUrl(azureBlobUri);
      })
  );
  galleryItems = computed(() =>
    this.allImagesRaw()
      .filter(image => (image.base64Image || image.resourceUri) && !image.deleted)
      .map(image => {
        let azureBlobUri = "";
        if (image.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(image.base64Image));
        } else if (image.resourceUri) {
          azureBlobUri = image.resourceUri;
        }
        return new BqaImageItem({
          azureBlobUri: azureBlobUri || "",
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      })
  );

  private objectUrls = new Set<string>();

  dialog = inject(Dialog);

  constructor(
    private sanitizer: DomSanitizer,
    private blobService: BlobService,
    public imageCompressionPipelineService: ImageCompressionPipelineService
  ) { }

  ngOnDestroy(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  get footerButtons(): VerticalButton[] {
    const compressing = this.imageCompressionPipelineService.isCompressingImages();
    return [
      {
        label: compressing ? "Optimizing Images..." : "Save",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => this.onSave(),
        testId: "complete-bottom-sheet-btn-save"
      },{
        label: "Cancel",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.close(),
        testId: "complete-bottom-sheet-btn-cancel"
      }
    ];
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted));
  }

  close(): void {
    this.addedImages.set([]);
    this.form.reset({ photos: [] });
    this.formSubmitted = false;
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  async onSave(): Promise<void> {
    this.formSubmitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    await this.imageCompressionPipelineService.waitForCompletion();
    const mediaToSave: Media[] = this.addedImages().map(image => {
      const { file: _file, ...media } = image as MediaForCompression;
      return media;
    });
    this.save.emit(mediaToSave);
  }

  openImagesModal(index: number): void {
    const dialogRef = this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: true,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(fileIdentifier),
        onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file),
        backdropClass: "images-modal-backdrop"
      }
    });

    dialogRef.closed.subscribe(_ => {
      console.log("The dialog was closed");
    });
  }

  async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const index = this.addedImages().findIndex(img => img.fileIdentifier === originalFileIdentifier);
    this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }
    const mediaRecords = await this.imageCompressionPipelineService.encode(files, this.itemIdentifier, ImageType.ConfirmationHomeowner);

    this.addedImages.set(insertAt(this.addedImages(), mediaRecords, insertIndex));
    this.syncPhotosControl();

    this.imageCompressionPipelineService.compress(mediaRecords, async compressedMediaRecords => {
      const updatedImages = this.addedImages().map(originalImage => {
        const compressedImage = compressedMediaRecords.find(m => m.fileIdentifier === originalImage.fileIdentifier);
        if (compressedImage) {
          const { file: _file, ...media } = compressedImage as MediaForCompression;
          return { ...originalImage, ...media } as Media;
        }
        return originalImage;
      });
      this.addedImages.set(updatedImages);
      this.syncPhotosControl();
    });

    return mediaRecords;
  }

  handleImageDeleted(fileIdentifier: string): void {
    this.addedImages.set(this.addedImages().filter(img => img.fileIdentifier !== fileIdentifier));
    this.syncPhotosControl();
  }

  /** Mirror the captured (non-deleted) images into the form control so validation reflects them. */
  private syncPhotosControl(): void {
    this.form.controls.photos.setValue(this.allImagesRaw(), { emitEvent: false });
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }

  
}
